/**
 * NavigationPreview Component for UI-Model-1
 *
 * Displays a preview of the navigation bar showing groups and pages.
 * Allows selecting active group/page for canvas editing.
 * Supports adding new groups and pages via command pattern.
 */

import React, { useRef, useEffect } from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { createAddGroupCommand, createAddPageCommand } from '../../types/command.types';
import { createGroupAPI, createPageAPI } from '../../types/state.types';
import { generateGroupId, generatePageId, generateCanvasId } from '../../utils/idGenerator';
import { EyeIconDisable, HideEyeIcon } from 'assests/GmsIcons';

/** Check if click target is an interactive element (buttons, etc.) that should not trigger navigation select */
const isInteractiveElement = (target: HTMLElement): boolean =>
  target.tagName === 'BUTTON' ||
  target.closest('[data-ignore-click="true"]') !== null ||
  target.closest('button') !== null ||
  target.getAttribute('role') === 'button';

/**
 * Two jobs, two marks (2026-09-09, on the user's eye — twice).
 *
 * ACTIVE (the page the canvas shows) is the design system's own selection pill —
 * `accent-tint` fill under `accent` ink, border painted in the TINT so the box keeps
 * its size with no visible line. It is what Menu rows, Dropdown options, ChoiceChip
 * and the Tab badge wear; none of them outline a selected thing in brand navy.
 *
 * SELECTED (the element the config panel is editing) is a thin editor marquee: a 1px
 * `accent` hairline sitting 2px OUTSIDE the pill, on a surface-coloured gap. It reads
 * as "the panel is on this", not as the tab's border, and it leaves when a container
 * is selected while the pill stays.
 *
 * History, so nobody re-derives it: the tray used `tw-ring-2 tw-ring-accent` (once
 * `ring-blue-500`) stacked on a 1px accent border — three pixels of L* 23 navy around
 * a 30px tab, "too dominating". A same-colour halo (`ring-accent-tint`) was tried
 * next and read as a fatter pill with a dark line floating inside it, because the
 * body is already tint. Total dark ink is now one hairline, separated by white.
 */
const SELECTION_MARQUEE = 'tw-ring-1 tw-ring-accent tw-ring-offset-2 tw-ring-offset-surface';

/** Compute group button className based on selection and active state */
const getGroupButtonClassName = (
  groupId: string,
  activeGroupId: string | null | undefined,
  isSelected: boolean,
  textAlignmentClass: string,
): string => {
  const isActive = groupId === activeGroupId;
  // Selection never touches the border — one border expression, no competing classes.
  return `tw-px-2.5 tw-flex tw-gap-2 tw-py-0.5 tw-text-xs tw-font-medium tw-transition-colors tw-duration-200 tw-ease-in-out tw-rounded
    ${isSelected ? SELECTION_MARQUEE : ''}
    ${isActive
      ? 'tw-bg-accent-tint tw-text-accent tw-border tw-border-accent-tint'
      : 'tw-text-ink-secondary hover:tw-bg-surface-hover'}
    ${textAlignmentClass}`;
};

/** Compute page button className based on selection and active state */
const getPageButtonClassName = (
  pageId: string,
  groupId: string,
  activePageId: string | null | undefined,
  activeGroupId: string | null | undefined,
  isSelected: boolean,
  textAlignmentClass: string,
): string => {
  const isActive = pageId === activePageId && groupId === activeGroupId;
  return `tw-px-3.5 tw-py-1.5 tw-text-xs tw-border tw-rounded tw-flex-shrink-0
    ${isSelected ? SELECTION_MARQUEE : ''}
    ${isActive
      ? 'tw-bg-accent-tint tw-text-accent tw-border-accent-tint tw-shadow-sm tw-relative tw-z-10 tw-font-medium'
      : 'tw-bg-surface tw-text-ink-secondary tw-border-edge hover:tw-bg-surface-hover hover:tw-text-accent'}
    ${textAlignmentClass}`;
};

export type SelectedElementType = {
  type: 'navigation' | 'group' | 'page';
  id?: string;
  parentId?: string;
};

interface NavigationPreviewProps {
  id?: string;
  onNavigationSelect?: () => void;
  onGroupSelect?: (groupId: string) => void;
  onPageSelect?: (pageId: string, groupId: string) => void;
  selectedElement?: SelectedElementType;
}

export const NavigationPreview = ({
  id,
  onNavigationSelect,
  onGroupSelect,
  onPageSelect,
  selectedElement,
}: NavigationPreviewProps) => {
  const baseId = id || 'navigation-preview';
  const { state, dispatch } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const navigation = state.model?.layout.navigation;
  const { activeGroupId, activePageId } = state.editorState;
  const navContainerRef = useRef<HTMLDivElement>(null);

  // Extract navigation config from root level only (API-Direct architecture)
  // State structure matches API format - no nested configuration object
  const config = {
    orientation: navigation?.orientation ?? 'Horizontal',
    style: navigation?.style ?? 'Block',
    textAlignment: navigation?.textAlignment ?? 'Centre',
    showDivider: navigation?.showDivider ?? true,
    pageSpacing: navigation?.pageSpacing ?? 10,
    groupSpacing: navigation?.groupSpacing ?? 10,
    showNavigation: navigation?.showNavigation ?? true,
  };

  const groups = navigation?.groups || [];

  // Selection state checks - use context state for highlighting
  const { selectedSection, selectedGroupId, selectedPageId, selectedPageGroupId } = state.editorState;
  const isNavigationSelected = selectedSection === 'navigation';
  const isGroupSelected = (groupId: string) =>
    selectedGroupId === groupId && !selectedPageId;
  const isPageSelected = (pageId: string, groupId: string) =>
    selectedPageId === pageId && selectedPageGroupId === groupId;

  // Handle click on navigation background
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (navContainerRef.current && navContainerRef.current.contains(target)) {
        if (isInteractiveElement(target)) {
          return;
        }
        e.stopPropagation();
        // Set selection state for config panel
        dispatch({ type: 'SET_SELECTED_SECTION', payload: { section: 'navigation' } });
        // Call optional callback if provided
        if (onNavigationSelect) {
          onNavigationSelect();
        }
      }
    };

    const navContainer = navContainerRef.current;

    if (navContainer) {
      navContainer.addEventListener('click', handleClick as EventListener);
    }

    return () => {
      if (navContainer) {
        navContainer.removeEventListener('click', handleClick as EventListener);
      }
    };
  }, [onNavigationSelect]);

  // Add new group handler
  const handleAddGroup = () => {
    const newGroupId = generateGroupId();
    const newPageId = generatePageId();
    const newCanvasId = generateCanvasId(newPageId);

    const newPage = createPageAPI(newPageId, 'Page 1', newCanvasId, 0);

    // Find next unused group number to avoid duplicates after deletions
    const existingGroupNames = new Set(groups.map(g => g.name));
    let groupNumber = groups.length + 1;
    while (existingGroupNames.has(`Group ${groupNumber}`)) {
      groupNumber++;
    }

    const newGroup = createGroupAPI(newGroupId, `Group ${groupNumber}`, groups.length, {
      pages: [newPage],
    });

    const command = createAddGroupCommand(newGroup);
    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

    if (navigation) {
      const updatedNavigation = {
        ...navigation,
        groups: [...navigation.groups, newGroup],
      };
      enqueueSave('navigation', { navigationData: updatedNavigation });
    }

    // Set new group as active
    dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId: newGroupId } });
    dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: newPageId } });

    if (onNavigationSelect) {
      onNavigationSelect();
    }
  };

  // Add new page to group handler
  const handleAddPage = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    const newPageId = generatePageId();
    const newCanvasId = generateCanvasId(newPageId);
    const pageCount = group.pages?.length || 0;

    // Find next unused page number to avoid duplicates after deletions
    const existingPageNames = new Set(group.pages?.map(p => p.name) || []);
    let pageNumber = pageCount + 1;
    while (existingPageNames.has(`Page ${pageNumber}`)) {
      pageNumber++;
    }

    const newPage = createPageAPI(newPageId, `Page ${pageNumber}`, newCanvasId, pageCount);

    const command = createAddPageCommand(newPage, groupId);
    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'navigation' } });

    if (navigation) {
      const updatedGroups = navigation.groups.map((g) => {
        if (g.id !== groupId) return g;
        return { ...g, pages: [...g.pages, newPage] };
      });
      enqueueSave('navigation', { navigationData: { ...navigation, groups: updatedGroups } });
    }
  };

  // Select active group
  const handleSelectActiveGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group && group.pages && group.pages.length > 0) {
      dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId } });
      dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId: group.pages[0].id } });
      dispatch({ type: 'SET_SELECTED_CONTAINER', payload: { containerId: null } });
    }
  };

  // Select active page
  const handleSelectActivePage = (groupId: string, pageId: string) => {
    dispatch({ type: 'SET_ACTIVE_GROUP', payload: { groupId } });
    dispatch({ type: 'SET_ACTIVE_PAGE', payload: { pageId } });
    dispatch({ type: 'SET_SELECTED_CONTAINER', payload: { containerId: null } });
  };

  // Style helpers
  const getPageSpacingStyle = () => ({
    marginRight: `${config.pageSpacing}px`,
  });

  const getGroupSpacingStyle = () => ({
    marginRight: `${config.groupSpacing}px`,
  });

  const TEXT_ALIGNMENT_MAP: Record<string, string> = {
    Left: 'tw-text-left',
    Centre: 'tw-text-center',
    Right: 'tw-text-right',
  };
  const textAlignmentClass = TEXT_ALIGNMENT_MAP[config.textAlignment] || 'tw-text-center';

  // Empty state
  if (!navigation?.groups || navigation.groups.length === 0) {
    return (
      <div
        id={baseId}
        data-testid={baseId}
        ref={navContainerRef}
        className={`tw-bg-surface tw-w-full tw-relative tw-min-h-[80px] tw-border-2
          ${isNavigationSelected ? 'tw-border-accent' : 'tw-border-transparent'}`}
      >
        <div
          id={`${baseId}-add-group-section`}
          className="tw-flex tw-flex-col tw-py-2 tw-ps-3 tw-pe-4 tw-space-y-3"
        >
          <button
            id={`${baseId}-add-group-btn`}
            data-testid={`${baseId}-add-group-btn`}
            data-ignore-click="true"
            className="tw-flex tw-items-center tw-justify-center tw-font-medium tw-text-xs tw-h-6 tw-px-2.5 tw-rounded tw-border tw-border-accent tw-bg-surface tw-text-accent hover:tw-bg-accent-tint"
            onClick={(e) => {
              e.stopPropagation();
              handleAddGroup();
            }}
          >
            <span className="tw-mr-1 tw-text-xs">+</span>
            <span className="tw-text-xs">Group</span>
          </button>

          <div
            id={`${baseId}-placeholder`}
            className="tw-border tw-bg-surface-sunken tw-border-dashed tw-border-edge-strong tw-h-7 tw-w-18 tw-rounded"
          />
        </div>
      </div>
    );
  }

  const showDividers = config.showDivider;

  return (
    <div className={`tw-relative ${config.showNavigation === false ? 'tw-min-h-[80px]' : ''}`}>
      <div
        id={baseId}
        data-testid={baseId}
        ref={navContainerRef}
        className={`tw-bg-surface tw-w-full tw-relative tw-border-2
          ${isNavigationSelected ? 'tw-border-accent' : 'tw-border-transparent'}`}
        style={{ overflow: 'hidden' }}
      >
        {/* Hidden overlay when showNavigation is false */}
        {config.showNavigation === false && (
          <div className="tw-absolute tw-inset-0 tw-bg-surface-sunken tw-flex tw-items-center tw-justify-center tw-z-10 tw-rounded-md">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-ink-secondary [&_path]:tw-stroke-current">
              <HideEyeIcon />
              <span
                className="tw-font-semibold tw-text-base"
                style={{ fontFamily: "'Noto Sans', sans-serif" }}
              >
                Navigation
              </span>
            </div>
          </div>
        )}

        <div
          id={`${baseId}-scroll-container`}
          className="tw-overflow-x-auto tw-overflow-y-hidden"
          style={{ width: '100%', scrollbarWidth: 'thin' }}
        >
          <div
            id={`${baseId}-groups-container`}
            className={`tw-flex tw-items-stretch tw-flex-nowrap ${config.orientation === 'Vertical' ? 'tw-flex-col' : ''}`}
          >
            {/* Render each group as a separate section */}
            {groups.map((group, index) => (
              <React.Fragment key={group.id}>
                {/* Divider between groups */}
                {index > 0 && showDividers && (
                  <div
                    id={`${baseId}-divider-${index}`}
                    className="tw-border-l tw-border-edge tw-self-stretch tw-mx-1.5 tw-flex-shrink-0"
                  />
                )}

                <div
                  id={`${baseId}-group-${group.id}`}
                  data-testid={`${baseId}-group-${group.id}`}
                  className="tw-flex tw-flex-col tw-py-2 tw-ps-3 tw-pe-3 tw-flex-shrink-0"
                  style={index < groups.length - 1 ? getGroupSpacingStyle() : undefined}
                >
                  {/* Group title */}
                  <div id={`${baseId}-group-${group.id}-title-wrapper`} className="tw-mb-2">
                    <button
                      id={`${baseId}-group-${group.id}-btn`}
                      data-testid={`${baseId}-group-${group.id}-btn`}
                      data-ignore-click="true"
                      className={getGroupButtonClassName(group.id, activeGroupId, isGroupSelected(group.id), textAlignmentClass)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onGroupSelect) {
                          onGroupSelect(group.id);
                        }
                        // Set selection state for config panel
                        dispatch({ type: 'SET_SELECTED_GROUP', payload: { groupId: group.id } });
                        handleSelectActiveGroup(group.id);
                      }}
                    >
                      {/* Hidden title indicator */}
                      {!group.configuration?.showTitle && <span className="tw-text-ink-secondary [&_path]:tw-stroke-current"><EyeIconDisable /></span>}
                      {!group.configuration?.showTitle && 'Title'}
                      {group.configuration?.showTitle !== false && group.name}
                    </button>
                  </div>

                  {/* Group pages - hidden when navigation is OFF */}
                  {config.showNavigation !== false && (
                  <div
                    id={`${baseId}-group-${group.id}-pages`}
                    className="tw-flex tw-items-center tw-gap-1.5"
                  >
                    {group.pages?.map((page, pageIndex) => (
                      <button
                        key={page.id}
                        id={`${baseId}-page-${page.id}`}
                        data-testid={`${baseId}-page-${page.id}`}
                        data-ignore-click="true"
                        className={getPageButtonClassName(page.id, group.id, activePageId, activeGroupId, isPageSelected(page.id, group.id), textAlignmentClass)}
                        style={pageIndex < (group.pages?.length || 0) - 1 ? getPageSpacingStyle() : undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onPageSelect) {
                            onPageSelect(page.id, group.id);
                          }
                          // Set selection state for config panel
                          dispatch({ type: 'SET_SELECTED_PAGE', payload: { pageId: page.id, groupId: group.id } });
                          handleSelectActivePage(group.id, page.id);
                        }}
                      >
                        {page.name}
                      </button>
                    ))}

                    {/* Add Page button for this group */}
                    <button
                      id={`${baseId}-group-${group.id}-add-page-btn`}
                      data-testid={`${baseId}-group-${group.id}-add-page-btn`}
                      data-ignore-click="true"
                      className="tw-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded tw-border tw-border-edge tw-bg-surface tw-text-ink-secondary tw-flex-shrink-0 hover:tw-bg-surface-hover hover:tw-text-accent"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPage(group.id);
                      }}
                      aria-label="Add Page"
                      title="Add Page"
                    >
                      <span className="tw-text-xs">+</span>
                    </button>
                  </div>
                  )}
                </div>
              </React.Fragment>
            ))}

            {/* Vertical divider before "+ Group" section */}
            {showDividers && groups.length > 0 && (
              <div
                id={`${baseId}-add-group-divider`}
                className="tw-border-l tw-border-edge tw-self-stretch tw-mx-1.5 tw-flex-shrink-0"
              />
            )}

            {/* "+ Group" section */}
            <div
              id={`${baseId}-add-group-section`}
              className="tw-flex tw-flex-col tw-py-2 tw-ps-2 tw-pe-4 tw-space-y-3 tw-self-end tw-flex-shrink-0"
            >
              <button
                id={`${baseId}-add-group-btn`}
                data-testid={`${baseId}-add-group-btn`}
                data-ignore-click="true"
                className="tw-flex tw-items-center tw-justify-center tw-font-medium tw-text-xs tw-h-6 tw-px-2.5 tw-rounded tw-border tw-border-accent tw-bg-surface tw-text-accent hover:tw-bg-accent-tint"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddGroup();
                }}
              >
                <span className="tw-mr-1 tw-text-xs">+</span>
                <span className="tw-text-xs">Group</span>
              </button>

              <div
                id={`${baseId}-placeholder`}
                className="tw-border tw-bg-surface-sunken tw-border-dashed tw-border-edge-strong tw-h-7 tw-w-18 tw-rounded"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationPreview;
